import {
	Dispatch,
	ReactElement,
	RefObject,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { HubOverviewLayout, HubOverviewLayoutSectionContainerHandle } from "../HubOverviewLayout";
import {
	BusyIndicator,
	ContextActionBar,
	FloatingActionButton,
	getPropertySortText,
	NoResults,
	OptionsMenu,
	OptionsMenuItem,
	SearchInputForm,
	SearchPlaceholder,
	ToolbarAction,
	ToolbarRegularAction,
	useConfirmationDialog
} from "client/Components";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	useAlerts,
	useForceRerender,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import { newGuid } from "client_legacy/ndaw/guid";
import {
	DataSourceSortableState,
	DataSourceSortDirection,
	nativeSortFunction
} from "client/Data/sorting";
import {
	CommunityDiscoverPageModel,
	CommunityDiscoveryListItem,
	CommunityKind,
	deleteCommunity,
	discoverCommunities,
	joinCommunity,
	leaveCommunity,
	markCommunityAsSeen
} from "client/Api/Communities/community";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { contains } from "client/Data/searching";
import { ListLayout } from "../ListLayout/ListLayout";
import { CommunityCard } from "./CommunityCard";
import {
	EMPTY_ARRAY,
	getPropertyPathResolver,
	isNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { CommunityEvent, publish } from "./events";
import { SaveCommunityResult, useEditCommunityDialog } from "./EditCommunityDialog/EditCommunityDialog";
import { getHubNavigationForCommunity } from "./CommunitiesPage";
import { HubDataSource } from "client/Api/Hub/hub";
import { Navigation, NavigationCallback } from "client/Navigation";
import { useCommunityDeletionConfirmationDialog } from "./CommunityDeletionConfirmationDialog";
import { useCommunityMembersDialog } from "./CommunityMembersDialog";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import {
	HubEvent,
	publish as publishHubEvent,
	subscribe,
	unsubscribe
} from "../events";

export type DiscoverPageProps = {
	onNavigate: NavigationCallback;
};

export const DiscoverPage = (props: DiscoverPageProps): ReactElement => {
	const {
		onNavigate
	} = props;

	const [communities, setCommunities] = useState<readonly CommunityDiscoveryListItem[] | null>(null);
	const [canCreateNewCommunity, setCanCreateNewCommunity] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>(() => ({ value: "", version: newGuid() }));
	const [sortingState, setSortingState] = useState<SortingState>(sortOptions[0].value);
	const [isBusy, setIsBusy] = useState<boolean>(true);

	const containerRef: RefObject<HubOverviewLayoutSectionContainerHandle | null> = useRef<HubOverviewLayoutSectionContainerHandle>(null);

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [deleteCommunityConfirmationDialog, openDeleteCommunityConfirmationDialog] = useCommunityDeletionConfirmationDialog();
	const [editDialog, openEditDialog] = useEditCommunityDialog();
	const [reload, loadVersion] = useForceRerender();
	const [pendingMembershipChanges, setPendingMembershipChanges] = useState<ReadonlySet<number>>(new Set<number>());
	const [membersDialog, openMembersDialog] = useCommunityMembersDialog();

	const sortedCommunities = useMemo(
		(): readonly CommunityDiscoveryListItem[] => {
			if (isNil(communities)) {
				return EMPTY_ARRAY;
			}

			const propertyResolver: (value: CommunityDiscoveryListItem) => string = getPropertyPathResolver(
				sortingState.propertyName
			);

			return [...communities].sort(
				(a: CommunityDiscoveryListItem, b: CommunityDiscoveryListItem): number => {
					if (a.kind === CommunityKind.AllCompany) {
						return -1;
					}

					if (b.kind === CommunityKind.AllCompany) {
						return 1;
					}

					return nativeSortFunction(
						propertyResolver(a),
						propertyResolver(b),
						sortingState.direction
					);
				}
			);
		},
		[sortingState, communities]
	);

	const {
		currentItems
	} = useDataSource(
		sortedCommunities,
		{
			searchTerm: searchTerm.value
		}
	);

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(loadVersion);

			const load = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const pageModel: CommunityDiscoverPageModel = await discoverCommunities(signal);

					setCanCreateNewCommunity(pageModel.canCreateNewCommunity);
					setCommunities(pageModel.communities);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[alertManager, loadVersion]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(searchTerm, sortingState);

			containerRef.current
				?.scrollToTop();
		},
		[searchTerm, sortingState]
	);

	const createNewHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<SaveCommunityResult> = await openEditDialog({});
			if (!result.wasSuccessful) {
				return;
			}

			reload();
		},
		[openEditDialog, reload]
	);

	const clearCommunityNewStatusHandler = useCallback(
		async (communityId: number): Promise<void> => {
			publishHubEvent(
				HubEvent.IsNewStatusCleared,
				{
					kind: HubDataSource.Community,
					communityId
				}
			);

			try {
				await markCommunityAsSeen(communityId, globalCancellation);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
			}
		},
		[globalCancellation]
	);

	const viewFeedHandler = useCallback(
		(id: number): void => {
			clearCommunityNewStatusHandler(id);

			const navigation: Navigation = getHubNavigationForCommunity({
				source: HubDataSource.Community,
				communityId: id
			});

			onNavigate(navigation);
		},
		[onNavigate, clearCommunityNewStatusHandler]
	);

	const joinHandler = useCallback(
		async (id: number): Promise<void> => {
			try {
				clearCommunityNewStatusHandler(id);

				setPendingMembershipChanges((x): ReadonlySet<number> => new Set<number>(x).add(id));

				const newMemberCount: number = await joinCommunity(id, globalCancellation);

				mutateUserMembership(
					setCommunities,
					id,
					true,
					newMemberCount
				);

				const community: CommunityDiscoveryListItem | undefined= communities?.find((x) => x.id === id);

				if (community) {
					const {
						name
					} = community;

					publish(
						CommunityEvent.JoinedCommunity,
						{ id, name }
					);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setPendingMembershipChanges(
				(x): ReadonlySet<number> => {
					const result = new Set<number>(x);
					result.delete(id);
					return result;
				}
			);
		},
		[
			alertManager,
			clearCommunityNewStatusHandler,
			communities,
			globalCancellation
		]
	);

	const leaveHandler = useCallback(
		async (id: number): Promise<void> => {
			try {
				const result: TryResult<void> = await openConfirmationDialog({
					trackingId: "leave-community-confirmation-dialog",
					title: localize("_AreYouSureYouWantToLeaveThisCommunity"),
					confirmText: localize("_Leave")
				});

				if (!result.wasSuccessful) {
					return;
				}

				setPendingMembershipChanges((x): ReadonlySet<number> => new Set<number>(x).add(id));

				const newMemberCount: number = await leaveCommunity(id, globalCancellation);

				mutateUserMembership(
					setCommunities,
					id,
					false,
					newMemberCount
				);

				publish(CommunityEvent.LeftCommunity, { communityId: id });
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setPendingMembershipChanges(
				(x): ReadonlySet<number> => {
					const result = new Set<number>(x);
					result.delete(id);
					return result;
				}
			);
		},
		[
			alertManager,
			globalCancellation,
			openConfirmationDialog
		]
	);

	const editHandler = useCallback(
		async (id: number): Promise<void> => {
			clearCommunityNewStatusHandler(id);

			const result: TryResult<SaveCommunityResult> = await openEditDialog({
				communityId: id
			});

			if (!result.wasSuccessful) {
				return;
			}

			reload();
		},
		[
			clearCommunityNewStatusHandler,
			openEditDialog,
			reload
		]
	);

	const deleteHandler = useCallback(
		async (id: number): Promise<void> => {
			clearCommunityNewStatusHandler(id);

			const result: TryResult<void> = await openDeleteCommunityConfirmationDialog();

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				await deleteCommunity(id, globalCancellation);
				alertManager.showSuccess(localize("_ChangesSaved"));
				setCommunities((x) => x?.filter((c) => c.id !== id) ?? null);

				publish(CommunityEvent.CommunityDeleted, { communityId: id });
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsBusy(false);
		},
		[
			alertManager,
			clearCommunityNewStatusHandler,
			globalCancellation,
			openDeleteCommunityConfirmationDialog
		]
	);

	const showMembersHandler = useCallback(
		(
			communityId: number,
			membersCount: number,
		): void => {
			clearCommunityNewStatusHandler(communityId);

			openMembersDialog({
				communityId,
				membersCount
			});
		},
		[clearCommunityNewStatusHandler, openMembersDialog]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<CommunityDiscoverActionContext> => {
			return getNewAction(createNewHandler);
		},
		[createNewHandler]
	);

	const topBarActions = useMemo(
		(): readonly CommunityDiscoverAction[] => {
			return buildActionsForTopBar<CommunityDiscoverActionContext, CommunityDiscoverAction>(
				{
					mainActions: [mainAction]
				},
				false,
				isResponsive,
				false
			);
		},
		[isResponsive, mainAction]
	);

	const actionContext = useMemo(
		(): CommunityDiscoverActionContext => ({
			canCreateNewCommunity
		}),
		[canCreateNewCommunity]
	);

	const fabHandler = useMemo(
		(): (() => void) | undefined => {
			if (mainAction.canExecute(actionContext)) {
				return (): void => mainAction.execute(actionContext);
			}

			return undefined;
		},
		[actionContext, mainAction]
	);

	useEffect(
		(): (() => void) => {
			const isNewStatusClearedToken: PubSubSubscriptionToken = subscribe(
				HubEvent.IsNewStatusCleared,
				(_, data) => {
					if (data.kind !== HubDataSource.Community) {
						return;
					}

					setCommunities(
						(x): readonly CommunityDiscoveryListItem[] | null => {
							if (!x) {
								return x;
							}

							return x.map(
								(x): CommunityDiscoveryListItem => {
									if (x.id === data.communityId) {
										return {
											...x,
											userInfo: {
												...x.userInfo,
												isNew: false
											}
										};
									}

									return x;
								}
							);
						}
					);
				}
			);

			return (): void => unsubscribe(isNewStatusClearedToken);
		},
		[]
	);

	const renderContent = (): ReactElement | null => {
		if (communities === null) {
			return null;
		}

		if (currentItems.length > 0) {
			return (
				<>
					<ListLayout
						trackingId="communities"
						hasMobileSpacing
					>
						{currentItems.map((x): ReactElement => (
							<ListLayout.Item key={x.id}>
								<CommunityCard
									item={x}
									isChangingMembership={pendingMembershipChanges.has(x.id)}
									onNavigateToFeed={() => viewFeedHandler(x.id)}
									onJoin={() => joinHandler(x.id)}
									onLeave={() => leaveHandler(x.id)}
									onEdit={() => editHandler(x.id)}
									onDelete={() => deleteHandler(x.id)}
									onShowMembers={() => showMembersHandler(x.id, x.membersCount)}
								/>
							</ListLayout.Item>
						))}
					</ListLayout>
					<AiAssistantFabSpacing />
				</>
			);
		}

		return (
			<NoResults
				trackingId="no-search-results"
				icon="magnifying-glass"
				title={localize("_NoSearchResults")}
				message={localize("_PleaseTrySearchingWithAnotherTerm")}
				height="fill"
			/>
		);
	};

	return (
		<>
			<HubOverviewLayout trackingId="communities-discover">
				{isMobile && (
					<ContextActionBar
						actions={topBarActions}
						context={actionContext}
						hasBorderBottom
						trackingId="global-action-bar"
					/>
				)}

				<HubOverviewLayout.Content>
					{isBusy && (
						<BusyIndicator />
					)}

					<HubOverviewLayout.Header
						trackingId="header"
						activeFilter={null}
						onClearActiveFilter={() => { /* NOOP */}}
						title={localize("_DiscoverCommunities")}
						actions={topBarActions}
						actionContext={actionContext}
					>
						<SearchInputForm
							value={searchTerm}
							onChange={(value, version) => setSearchTerm({ value, version })}
							placeholder={
								<SearchPlaceholder
									kind="filter"
									properties={[
										localize("_Name")
									]}
								/>
							}
							trackingId="search-input"
							height="large"
							icon="filter"
						/>

						<OptionsMenu
							title={localize("_SortBy")}
							trackingId="sort-by"
							value={sortingState}
							options={sortOptions}
							onChange={setSortingState}
							icon="arrow-up-arrow-down"
							buttonSize="large"
						/>
					</HubOverviewLayout.Header>

					<HubOverviewLayout.SectionContainer
						trackingId="section-container"
						ref={containerRef}
					>
						{renderContent()}
						<HubOverviewLayout.MobilePlusMenuSpacing />
					</HubOverviewLayout.SectionContainer>

					{fabHandler && (
						<FloatingActionButton
							icon="plus"
							onClick={fabHandler}
							trackingId="create-community-fab"
						/>
					)}
				</HubOverviewLayout.Content>
			</HubOverviewLayout>

			{confirmationDialog}
			{deleteCommunityConfirmationDialog}
			{editDialog}
			{membersDialog}
		</>
	);
};
DiscoverPage.displayName = "DiscoverPage";

type CommunityDiscoverActionContext = {
	canCreateNewCommunity: boolean;
};

type CommunityDiscoverAction = ToolbarAction<CommunityDiscoverActionContext>;

function getNewAction(
	handler: () => void
): ToolbarRegularAction<CommunityDiscoverActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "create-community",
		title: localize("_New"),
		icon: "plus",
		isPrimary: true,
		canExecute: (x): boolean => x.canCreateNewCommunity,
		execute: (): void => handler()
	};
}

type SortingState = Exclude<
	DataSourceSortableState<Pick<CommunityDiscoveryListItem, "name">>,
	{ direction: DataSourceSortDirection.None; }
>;

type SortByOptions = OptionsMenuItem<SortingState>;

const sortOptions: readonly SortByOptions[] = [
	{
		text: getPropertySortText("_Name", "_AToZ"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "name-a-z"
	},
	{
		text: getPropertySortText("_Name", "_ZToA"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "name-z-a"
	}
];

const useDataSource = createLocalDataSource<CommunityDiscoveryListItem>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) => contains(item.name, searchTerm),
		}
	})
	.getHook();

function mutateUserMembership(
	setCommunities: Dispatch<SetStateAction<readonly CommunityDiscoveryListItem[] | null>>,
	communityId: number,
	isMember: boolean,
	membersCount: number
) {
	setCommunities(
		(x): readonly CommunityDiscoveryListItem[] | null => {
			if (!x) {
				return x;
			}

			return x.map((x) => {
				if (x.id === communityId) {
					return {
						...x,
						membersCount,
						userInfo: {
							...x.userInfo,
							isMember
						}
					};
				}

				return x;
			});
		}
	);
}
